import { EndpointBindingType, SSOConfig, SSOType } from "@app/sso-schemes";

export interface SSO {
  isEnabled: boolean;
  schemeName: string;
  displayName: string;
  ssoType: SSOType;
  callbackPath: string;
  emailClaimType: string;
  nameIdentifierClaimType: string;

  // identity provider options
  idpEntityId?: string;
  idpSingleSignOnEndpoint?: string;
  idpSignOnEndpointBindingType?: EndpointBindingType;
  idpSigningCertificatesBase64?: string[];

  // service provider options
  spEntityId?: string;
  spRequireEncryptedAssertions?: boolean;
  spEncryptionCertificateBase64?: string;
  spSignAuthenticationRequests?: boolean;
  spSigningCertificateBase64?: string;
  spMetadataPath?: string;
}

export function toSSO(ssoConfig: SSOConfig): SSO {
  const result: SSO = { ...ssoConfig };
  if (ssoConfig) {
    result.emailClaimType = ssoConfig.emailClaimType;
    result.nameIdentifierClaimType = ssoConfig.nameIdentifierClaimType;
    result.ssoType = ssoConfig.ssoType;

    // identity provider options
    if (ssoConfig.idPOptions) {
      result.idpEntityId = ssoConfig.idPOptions.entityId;
      result.idpSignOnEndpointBindingType = ssoConfig.idPOptions.signOnEndpointBindingType;
      result.idpSigningCertificatesBase64 = ssoConfig.idPOptions.signingCertificatesBase64;
      result.idpSingleSignOnEndpoint = ssoConfig.idPOptions.singleSignOnEndpoint;
    }

    // service provider options
    if (ssoConfig.spOptions) {
      result.spEncryptionCertificateBase64 = ssoConfig.spOptions.encryptionCertificateBase64;
      result.spEntityId = ssoConfig.spOptions.entityId;
      result.spMetadataPath = ssoConfig.spOptions.metadataPath;
      result.spRequireEncryptedAssertions = ssoConfig.spOptions.requireEncryptedAssertions;
      result.spSignAuthenticationRequests = ssoConfig.spOptions.signAuthenticationRequests;
      result.spSigningCertificateBase64 = ssoConfig.spOptions.signingCertificateBase64;
    }
  }
  return result;
}

export function toSSOConfig(sso: SSO, clientId: number): SSOConfig {

  const ssoConfig: SSOConfig = {
    // minimum required elements for SSOConfig
    clientId,
    idPOptions: undefined,
    spOptions: undefined,
    ...sso,
  };

  // only set idpOptions and spOptions if any of those values is set. Otherwise it should remain undefined
  if (sso.idpEntityId || sso.idpSigningCertificatesBase64 || sso.idpSignOnEndpointBindingType || sso.idpSingleSignOnEndpoint) {
    ssoConfig.idPOptions = {
      entityId: sso.idpEntityId,
      signingCertificatesBase64: sso.idpSigningCertificatesBase64,
      signOnEndpointBindingType: sso.idpSignOnEndpointBindingType,
      singleSignOnEndpoint: sso.idpSingleSignOnEndpoint,
    };
  }

  if (sso.spEncryptionCertificateBase64 || sso.spEntityId || sso.spMetadataPath || sso.spRequireEncryptedAssertions || sso.spSignAuthenticationRequests || sso.spSigningCertificateBase64) {
    ssoConfig.spOptions = {
      encryptionCertificateBase64: sso.spEncryptionCertificateBase64,
      entityId: sso.spEntityId,
      metadataPath: sso.spMetadataPath,
      requireEncryptedAssertions: sso.spRequireEncryptedAssertions,
      signAuthenticationRequests: sso.spSignAuthenticationRequests,
      signingCertificateBase64: sso.spSigningCertificateBase64,
    };
  }

  return ssoConfig;
}
